// Enhanced JB Racks Customer Segmentation System with Better Klaviyo Detection
      class JBCustomerSegmentation {
        constructor() {
          this.segmentConfig = {
            mountain_biking: {
              segment: 'Mountain Biker',
              'segment-plural': 'Mountain Bikers',
              'segment-for': 'for Mountain Bikers',
              'segment-for-single': 'for a Mountain Biker',
              'segment-item': 'Mountain Bikes',
            },
            family_adventures: {
              segment: 'Family Adventurer',
              'segment-plural': 'Family Adventurers',
              'segment-for': 'for Family Adventurers',
              'segment-for-single': 'for a Family Adventurer',
              'segment-item': 'Families',
            },
            road_cycling: {
              segment: 'Road Cyclist',
              'segment-plural': 'Road Cyclists',
              'segment-for': 'for Road Cyclists',
              'segment-for-single': 'for a Road Cyclist',
              'segment-item': 'Cyclists',
            },
            commuting: {
              segment: 'Commuter',
              'segment-plural': 'Commuters',
              'segment-for': 'for Commuters',
              'segment-for-single': 'for a Commuter',
              'segment-item': 'Commuter Bikes',
            },
          };

          this.popup = null;
          this.isVisible = false;
          this.klaviyoChecked = false;
          this.klaviyoRetryCount = 0;
          this.maxKlaviyoRetries = 10;
          this.init();
        }

        getSessionId() {
          let sessionId = sessionStorage.getItem('jb_session_id');
          if (!sessionId) {
            sessionId = 'sess_' + Math.random().toString(36).substr(2, 16) + Date.now().toString(36);
            sessionStorage.setItem('jb_session_id', sessionId);
          }
          return sessionId;
        }

        getBrowserFingerprint() {
          let fingerprint = localStorage.getItem('jb_browser_fingerprint');
          if (!fingerprint) {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            ctx.textBaseline = 'top';
            ctx.font = '14px Arial';
            ctx.fillText('JB Racks fingerprint', 2, 2);

            fingerprint = btoa(
              JSON.stringify({
                userAgent: navigator.userAgent.substring(0, 100),
                language: navigator.language,
                platform: navigator.platform,
                screen: `${screen.width}x${screen.height}`,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                canvas: canvas.toDataURL().substring(0, 50),
              })
            ).substring(0, 32);

            localStorage.setItem('jb_browser_fingerprint', fingerprint);
          }
          return fingerprint;
        }

        // ENHANCED: Multiple methods to detect Klaviyo users
        async checkKlaviyoUser() {
          return new Promise((resolve) => {
            const checkMethods = [
              () => this.checkKlaviyoProfile(),
              () => this.checkKlaviyoTracking(),
              () => this.checkKlaviyoGlobal(),
              () => this.checkKlaviyoCookies(),
              () => this.checkKlaviyoLocalStorage(),
            ];

            let methodIndex = 0;
            const tryNextMethod = () => {
              if (methodIndex >= checkMethods.length) {
                resolve(null);
                return;
              }

              const result = checkMethods[methodIndex]();
              if (result) {
                console.log(`Klaviyo user found via method ${methodIndex + 1}:`, result);
                resolve(result);
              } else {
                methodIndex++;
                setTimeout(tryNextMethod, 100);
              }
            };

            tryNextMethod();
          });
        }

        // Method 1: Check Klaviyo profile object
        checkKlaviyoProfile() {
          try {
            if (window._learnq && window._learnq.profile) {
              const profile = window._learnq.profile;
              return {
                email: profile.$email,
                customerType: profile.$custom_properties?.customer_type,
                klaviyoId: profile.$id,
                source: 'profile_object',
              };
            }
          } catch (e) {
            console.log('Klaviyo profile check error:', e);
          }
          return null;
        }

        // Method 2: Check Klaviyo tracking state
        checkKlaviyoTracking() {
          try {
            if (typeof klaviyo !== 'undefined') {
              // Try to get user info via the tracking object
              const trackingData = klaviyo._get && klaviyo._get('user');
              if (trackingData) {
                return {
                  email: trackingData.email,
                  customerType: trackingData.customer_type,
                  source: 'tracking_object',
                };
              }

              // Check if klaviyo has identified user
              if (klaviyo.isIdentified && typeof klaviyo.isIdentified === 'function') {
                let identifiedData = null;
                klaviyo.isIdentified(function (identified, profile) {
                  if (identified && profile) {
                    identifiedData = {
                      email: profile.$email,
                      customerType: profile.$custom_properties?.customer_type,
                      klaviyoId: profile.$id,
                      source: 'identified_callback',
                    };
                  }
                });
                return identifiedData;
              }
            }
          } catch (e) {
            console.log('Klaviyo tracking check error:', e);
          }
          return null;
        }

        // Method 3: Check global Klaviyo variables
        checkKlaviyoGlobal() {
          try {
            // Check various global variables Klaviyo might set
            const globalVars = ['__klKey', '__klUser', '_learnq_profile', 'KlaviyoSubscribe'];

            for (const varName of globalVars) {
              if (window[varName]) {
                const data = window[varName];
                if (data && (data.email || data.$email)) {
                  return {
                    email: data.email || data.$email,
                    customerType: data.customer_type || data.$custom_properties?.customer_type,
                    source: `global_${varName}`,
                  };
                }
              }
            }
          } catch (e) {
            console.log('Klaviyo global check error:', e);
          }
          return null;
        }

        // Method 4: Check Klaviyo cookies
        checkKlaviyoCookies() {
          try {
            const cookies = document.cookie.split(';');
            for (const cookie of cookies) {
              const [name, value] = cookie.trim().split('=');

              // Look for Klaviyo-related cookies
              if (name.includes('klav') || name.includes('_kl') || name.includes('__kla')) {
                try {
                  const decoded = decodeURIComponent(value);
                  const parsed = JSON.parse(decoded);
                  if (parsed && (parsed.email || parsed.$email)) {
                    return {
                      email: parsed.email || parsed.$email,
                      customerType: parsed.customer_type,
                      source: `cookie_${name}`,
                    };
                  }
                } catch (e) {
                  // Cookie might not be JSON, skip
                }
              }
            }
          } catch (e) {
            console.log('Klaviyo cookie check error:', e);
          }
          return null;
        }

        // Method 5: Check localStorage for Klaviyo data
        checkKlaviyoLocalStorage() {
          try {
            const keys = Object.keys(localStorage);
            for (const key of keys) {
              if (key.includes('klav') || key.includes('_kl') || key.includes('__kla')) {
                try {
                  const value = localStorage.getItem(key);
                  const parsed = JSON.parse(value);
                  if (parsed && (parsed.email || parsed.$email)) {
                    return {
                      email: parsed.email || parsed.$email,
                      customerType: parsed.customer_type || parsed.$custom_properties?.customer_type,
                      source: `localStorage_${key}`,
                    };
                  }
                } catch (e) {
                  // Not JSON, skip
                }
              }
            }
          } catch (e) {
            console.log('Klaviyo localStorage check error:', e);
          }
          return null;
        }

        // ENHANCED: Retry mechanism for Klaviyo detection
        async retryKlaviyoDetection() {
          if (this.klaviyoChecked || this.klaviyoRetryCount >= this.maxKlaviyoRetries) {
            return null;
          }

          this.klaviyoRetryCount++;
          console.log(`Klaviyo detection attempt ${this.klaviyoRetryCount}/${this.maxKlaviyoRetries}`);

          const userData = await this.checkKlaviyoUser();

          if (userData) {
            this.klaviyoChecked = true;
            this.handleKlaviyoUserFound(userData);
            return userData;
          }

          // If Klaviyo still loading, try again
          if (this.klaviyoRetryCount < this.maxKlaviyoRetries) {
            setTimeout(() => this.retryKlaviyoDetection(), 1000);
          }

          return null;
        }

        // Handle when Klaviyo user is found
        handleKlaviyoUserFound(userData) {
          console.log('Klaviyo user detected:', userData);

          // Store the data
          if (userData.email) {
            localStorage.setItem('jb_customer_email', userData.email);
          }

          if (userData.customerType && userData.customerType !== 'unknown') {
            localStorage.setItem('jb_customer_type', userData.customerType);
            console.log('Customer type loaded from Klaviyo:', userData.customerType);
            this.updateSegmentContent();
          }

          // Send tracking event
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'klaviyo_user_detected',
            customer_email: userData.email,
            customer_type: userData.customerType,
            detection_source: userData.source,
            page_location: window.location.href,
          });
        }

        syncWithShopifyCustomer() {
          try {
            // Method 1: Check Shopify Analytics (most reliable)
            if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page) {
              const customerId = window.ShopifyAnalytics.meta.page.customerId;
              const customerEmail = window.ShopifyAnalytics.meta.page.customerEmail;

              if (customerId) {
                localStorage.setItem('jb_customer_id', customerId);
                if (customerEmail) {
                  localStorage.setItem('jb_customer_email', customerEmail);
                  // Identify in Klaviyo with Shopify data
                  this.identifyKlaviyoUser(customerEmail);
                }

                console.log('Synced with Shopify customer:', customerId);
                return true;
              }
            }

            // Method 2: Check global customer object (backup)
            if (window.customer && window.customer.id) {
              localStorage.setItem('jb_customer_id', window.customer.id);
              if (window.customer.email) {
                localStorage.setItem('jb_customer_email', window.customer.email);
                this.identifyKlaviyoUser(window.customer.email);
              }
              return true;
            }

            // Method 3: Check meta tags (backup)
            const customerIdMeta = document.querySelector('meta[name="customer-id"]');
            const customerEmailMeta = document.querySelector('meta[name="customer-email"]');

            if (customerIdMeta) {
              localStorage.setItem('jb_customer_id', customerIdMeta.content);
              if (customerEmailMeta) {
                localStorage.setItem('jb_customer_email', customerEmailMeta.content);
                this.identifyKlaviyoUser(customerEmailMeta.content);
              }
              return true;
            }

            return false;
          } catch (error) {
            console.log('Error syncing with Shopify customer:', error);
            return false;
          }
        }

        // ENHANCED: Better Klaviyo identification
        identifyKlaviyoUser(email, additionalProps = {}) {
          if (!email || typeof klaviyo === 'undefined') return;

          const identifyData = {
            $email: email,
            $custom_properties: {
              customer_type: this.getCustomerType(),
              shopify_customer_id: localStorage.getItem('jb_customer_id'),
              browser_fingerprint: this.getBrowserFingerprint(),
              session_id: this.getSessionId(),
              last_seen: new Date().toISOString(),
              ...additionalProps,
            },
          };

          // Add phone if available
          const phone = localStorage.getItem('jb_customer_phone');
          if (phone) {
            identifyData.$phone_number = phone;
          }

          klaviyo.identify(identifyData);
          console.log('Identified user in Klaviyo:', email);
        }

        getCustomerType() {
          try {
            // Check localStorage first (persistent across sessions)
            const storedType = localStorage.getItem('jb_customer_type');
            if (storedType && storedType !== 'unknown') {
              return storedType;
            }

            // Fallback to session variable for demo
            if (typeof window.customerType !== 'undefined' && window.customerType !== 'unknown') {
              return window.customerType;
            }

            // Check dataLayer for GTM/GA4 data
            if (window.dataLayer) {
              const customerTypeEvent = window.dataLayer
                .slice()
                .reverse()
                .find((event) => event.customer_type);

              if (customerTypeEvent?.customer_type) {
                return customerTypeEvent.customer_type;
              }
            }

            return 'unknown';
          } catch (error) {
            console.log('Error getting customer type:', error);
            return 'unknown';
          }
        }

        // Send segmentation data to GA4 on every page load
        sendSegmentationTracking(type) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'customer_type_identified',
            customer_type: type,
            page_location: window.location.href,
            page_title: document.title,
            identification_method: 'existing_user',
          });
        }

        // Update all segment content on page
        updateSegmentContent() {
          const customerType = this.getCustomerType();

          if (customerType === 'unknown') return;

          const config = this.segmentConfig[customerType];
          if (!config) return;

          // Find all segment spans and update them
          const segmentTypes = ['segment', 'segment-plural', 'segment-for', 'segment-for-single', 'segment-item'];

          segmentTypes.forEach((className) => {
            const elements = document.querySelectorAll(`.${className}`);
            const text = config[className] || '';

            elements.forEach((element) => {
              if (text) {
                element.textContent = text;
                element.style.display = 'inline';
              } else {
                element.style.display = 'none';
              }
            });
          });
        }

        // Set customer type and update content
        setCustomerType(type) {
          // Store permanently in localStorage
          localStorage.setItem('jb_customer_type', type);
          localStorage.setItem('jb_profile_popup_shown', 'true');

          // Also set session variable for immediate use
          window.customerType = type;
          window.profilePopupShown = true;

          // Send selection event to GA4/GTM
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'customer_type_identified',
            customer_type: type,
            identification_method: 'popup_selection',
            page_location: window.location.href,
            customer_id: localStorage.getItem('jb_customer_id') || 'anonymous',
            session_id: this.getSessionId(),
          });

          // Update Klaviyo profile
          const customerEmail = localStorage.getItem('jb_customer_email');
          this.identifyKlaviyoUser(customerEmail, {
            customer_type: type,
            identified_via: 'popup_selection',
            identification_date: new Date().toISOString(),
          });

          // Show success feedback
          this.showSuccessFeedback(type);

          // Hide popup after short delay
          setTimeout(() => {
            this.hidePopup();
          }, 1500);

          // Update content immediately
          this.updateSegmentContent();
        }

        // ENHANCED: Better initialization sequence
        async onReady() {
          this.popup = document.getElementById('jb-profile-popup');

          console.log('JB Segmentation initializing...');

          // 1. First, sync with Shopify customer data
          this.syncWithShopifyCustomer();

          // 2. Start Klaviyo detection with retries
          await this.retryKlaviyoDetection();

          // 3. Update content if customer type already known
          this.updateSegmentContent();

          // 4. If user is already segmented, send tracking data
          const customerType = this.getCustomerType();
          if (customerType !== 'unknown') {
            this.sendSegmentationTracking(customerType);
            console.log('User already segmented as:', customerType);
          }

          // 5. Show popup if needed
          this.showPopupIfNeeded();

          // 6. Setup page change monitoring
          this.setupPageChangeListener();

          // 7. Setup additional event listeners
          this.setupEventListeners();

          console.log('JB Segmentation initialized');
        }

        // NEW: Setup additional event listeners
        setupEventListeners() {
          // Listen for Klaviyo events
          window.addEventListener('klaviyoLoaded', () => {
            console.log('Klaviyo loaded event detected');
            this.retryKlaviyoDetection();
          });

          // Listen for form submissions to capture emails
          document.addEventListener('submit', (e) => {
            const emailInput = e.target.querySelector('input[type="email"]');
            if (emailInput && emailInput.value) {
              console.log('Email captured from form:', emailInput.value);
              localStorage.setItem('jb_customer_email', emailInput.value);
              this.identifyKlaviyoUser(emailInput.value);
            }
          });

          // Listen for newsletter signups specifically
          document.addEventListener('click', (e) => {
            if (e.target.matches('[data-email-signup], .newsletter-submit, .email-submit')) {
              setTimeout(() => {
                this.retryKlaviyoDetection();
              }, 500);
            }
          });
        }

        // Show success feedback
        showSuccessFeedback(type) {
          const popup = document.getElementById('jb-profile-popup');
          const config = this.segmentConfig[type];

          if (popup && config) {
            popup.classList.add('jb-popup-success');

            popup.innerHTML = `
        <div class="jb-popup-header">
          <div>
            <h3 class="jb-popup-title">Perfect!</h3>
            <p class="jb-popup-subtitle">We'll show you the best racks ${config['segment-for'].toLowerCase()}.</p>
          </div>
        </div>
      `;
          }
        }

        // Show popup with animation
        showPopup() {
          const popup = document.getElementById('jb-profile-popup');
          if (popup && !this.isVisible) {
            this.isVisible = true;
            popup.offsetHeight;
            popup.classList.add('show');
          }
        }

        // Hide popup with animation
        hidePopup() {
          const popup = document.getElementById('jb-profile-popup');
          if (popup && this.isVisible) {
            popup.classList.remove('show');
            this.isVisible = false;
          }
        }

        // Dismiss popup
        dismissProfile() {
          localStorage.setItem('jb_profile_popup_shown', 'true');
          window.profilePopupShown = true;
          this.hidePopup();
        }

        // Show popup logic with improved timing
        async showPopupIfNeeded() {
          // Don't show if customer type is already known (user is segmented)
          if (this.getCustomerType() !== 'unknown') {
            return;
          }

          // Don't show if popup was already dismissed permanently
          if (localStorage.getItem('jb_profile_popup_shown') === 'true') {
            return;
          }

          // Don't show if popup was already dismissed this session
          if (window.profilePopupShown) {
            return;
          }

          // Only show on relevant pages
          const currentPath = window.location.pathname;
          if (!currentPath.includes('/collections/') && !currentPath.includes('/products/') && currentPath !== '/') {
            return;
          }

          // Show after user has been on page for specified time or scrolled 30%
          setTimeout(() => {
            if (!this.isVisible) {
              this.showPopup();
            }
          }, 45000);
        }

        // Monitor for page changes (SPA support)
        setupPageChangeListener() {
          const originalPushState = history.pushState;
          const originalReplaceState = history.replaceState;

          history.pushState = (...args) => {
            originalPushState.apply(history, args);
            setTimeout(() => {
              this.updateSegmentContent();
              this.showPopupIfNeeded();
              const customerType = this.getCustomerType();
              if (customerType !== 'unknown') {
                this.sendSegmentationTracking(customerType);
              }
            }, 100);
          };

          history.replaceState = (...args) => {
            originalReplaceState.apply(history, args);
            setTimeout(() => {
              this.updateSegmentContent();
              this.showPopupIfNeeded();
              const customerType = this.getCustomerType();
              if (customerType !== 'unknown') {
                this.sendSegmentationTracking(customerType);
              }
            }, 100);
          };

          window.addEventListener('popstate', () => {
            setTimeout(() => {
              this.updateSegmentContent();
              this.showPopupIfNeeded();
              const customerType = this.getCustomerType();
              if (customerType !== 'unknown') {
                this.sendSegmentationTracking(customerType);
              }
            }, 100);
          });
        }

        // Initialize everything
        init() {
          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.onReady());
          } else {
            this.onReady();
          }
        }
      }

      // Initialize the system
      const jbSegmentation = new JBCustomerSegmentation();

      // Global functions for onclick handlers
      window.setJBCustomerType = (type) => jbSegmentation.setCustomerType(type);
      window.dismissJBProfile = () => jbSegmentation.dismissProfile();
    
    
      // Trigger custom event when Klaviyo loads
      if (typeof klaviyo !== 'undefined') {
        window.dispatchEvent(new CustomEvent('klaviyoLoaded'));
      }
